import React, { Component } from 'react'

import 'react-quill/dist/quill.snow.css'
import ReactQuill from 'react-quill'

import QuillMention from 'quill-mention'

import { isMobile } from 'utils'

// A deliberately lightweight Quill instance for chat messages. No toolbar,
// no markdown shortcuts, no Codox — just plain text plus @mentions and the
// occasional embedded image (Giphy). See quill-wysiwyg.jsx for the richer
// comment/note editor this is intentionally kept separate from.
export default class ChatMentionInput extends Component {
    quillRef = null

    // Only the mention span and images survive — everything else is treated as
    // plain text, so pasted rich content can't smuggle formatting into a chat.
    formats = ['mention', 'image']

    mentionModule = {
        allowedChars: /^[A-Za-z\s]*$/,
        mentionDenotationChars: ['@'],
        // The chat box lives at the bottom of the screen, so open the
        // suggestion list upward rather than pushing it off-screen.
        defaultMenuOrientation: 'top',
        source: (searchTerm, renderList) => {
            const values = this.props.availableMentions || []

            if (!searchTerm.length) {
                renderList(values, searchTerm)
                return
            }

            const matches = values.filter(value => (
                ~value.value.toLowerCase().indexOf(searchTerm.toLowerCase())
            ))

            renderList(matches, searchTerm)
        },
    }

    componentDidMount = _ => {
        const editor = this.getEditor()

        if (editor) {
            // Intercept Enter in the capture phase, *before* quill-mention's own
            // keyboard binding runs. quill-mention selects the highlighted user
            // and closes the list (isOpen -> false) synchronously, so if we
            // waited for React's onKeyDown the "is the list open?" check would
            // already read false and we'd send the message instead of picking
            // the mention.
            editor.root.addEventListener('keydown', this.handleEnterCapture, true)
        }
    }

    componentWillUnmount = _ => {
        const editor = this.getEditor()

        if (editor) {
            editor.root.removeEventListener('keydown', this.handleEnterCapture, true)
        }
    }

    getEditor = _ => (
        this.quillRef ? this.quillRef.getEditor() : null
    )

    handleEnterCapture = e => {
        if (isMobile()) {
            return
        }

        if (e.key !== 'Enter' || e.shiftKey) {
            return
        }

        // Mention list open: let quill-mention consume Enter to select a user.
        if (this.isMentionListOpen()) {
            return
        }

        // Stop propagation so Quill never inserts a newline, then send.
        e.preventDefault()
        e.stopPropagation()

        if (this.props.onEnter) {
            this.props.onEnter()
        }
    }

    // True while the @mention suggestion list is showing. Used by the parent so
    // pressing Enter picks a mention instead of firing off the message.
    isMentionListOpen = _ => {
        const editor = this.getEditor()

        if (!editor) {
            return false
        }

        const mention = editor.getModule('mention')

        return !!(mention && mention.isOpen)
    }

    focus = _ => {
        const editor = this.getEditor()

        if (editor) {
            editor.focus()
        }
    }

    // Insert text (an emoji) at the caret, falling back to the end of the doc.
    insertText = text => {
        const editor = this.getEditor()

        if (!editor) {
            return
        }

        const range = editor.getSelection(true)
        const index = range ? range.index : editor.getLength()

        editor.insertText(index, text, 'user')
        editor.setSelection(index + text.length, 0)
    }

    // Insert an image embed (a Giphy) at the caret.
    insertImage = url => {
        const editor = this.getEditor()

        if (!editor) {
            return
        }

        const range = editor.getSelection(true)
        const index = range ? range.index : editor.getLength()

        editor.insertEmbed(index, 'image', url, 'user')
        editor.setSelection(index + 1, 0)
    }

    // Note: Enter-to-send is handled in the capture-phase listener above, not
    // here — see handleEnterCapture for why. This only forwards key events.
    handleKeyDown = e => {
        if (this.props.onKeyDown) {
            this.props.onKeyDown(e)
        }
    }

    render() {
        const {
            value,
            onChange,
            onFocus,
            onBlur,
            placeholder,
            disabled
        } = this.props

        return (
            <div className="chat-quill-container">
                <ReactQuill
                    ref={ref => this.quillRef = ref}
                    value={value}
                    onChange={onChange}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    onKeyDown={this.handleKeyDown}
                    readOnly={disabled}
                    placeholder={placeholder || ''}
                    theme="snow"
                    className="chat-quill"
                    formats={this.formats}
                    modules={{
                        toolbar: false,
                        mention: this.mentionModule,
                        clipboard: { matchVisual: false },
                    }}
                />
            </div>
        )
    }
}
